import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Inject, Injectable, Optional, resource, Resource } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiErrorService } from './error';

export interface ApiRequestOptions {
  suppressToast?: boolean;
  responseType?: 'json' | 'text';
}

/**
 * Base API service providing common HTTP operations using Angular Resources
 * This service can be extended by feature services for specific API endpoints
 *
 * Example usage:
 * ```typescript
 * @Injectable({ providedIn: 'root' })
 * export class UserService extends BaseApiService<User> {
 *   constructor() {
 *     super('/api/users');
 *   }
 *
 *   // Create a resource for fetching users
 *   private usersResource = this.createGetResource<User[]>();
 *
 *   // Expose resource signals
 *   users = this.usersResource.value;
 *   loading = this.usersResource.isLoading;
 *   error = this.usersResource.error;
 *
 *   reloadUsers() {
 *     this.usersResource.reload();
 *   }
 *
 *   // Resource with reactive parameter
 *   private searchQuery = signal('');
 *   private searchResource = this.createGetResource<User[]>(
 *     '/search',
 *     () => this.searchQuery()
 *   );
 *
 *   searchResults = this.searchResource.value;
 *   searchLoading = this.searchResource.isLoading;
 * }
 * ```
 */
@Injectable()
export class BaseApiService<T> {
  protected readonly http: HttpClient;
  protected readonly errorService = inject(ApiErrorService);
  protected readonly baseUrlOrPromise: string | Promise<string>;

  constructor(@Optional() @Inject('API_BASE_URL') baseUrl?: string | Promise<string>) {
    this.http = inject(HttpClient);
    this.baseUrlOrPromise = baseUrl || '';
  }

  /**
   * Resolves the base URL, supporting both sync and async values
   */
  protected async getBaseUrl(): Promise<string> {
    return this.baseUrlOrPromise instanceof Promise ? await this.baseUrlOrPromise : this.baseUrlOrPromise;
  }

  /**
   * Create a resource for GET request
   * @param endpoint API endpoint (relative to baseUrl)
   * @param request Optional reactive request signal/function that triggers reload when changed
   */
  public createGetResource<R = T>(endpoint: string = '', request?: () => unknown): Resource<R> {
    return resource({
      request,
      loader: async () => {
        const baseUrl = await this.getBaseUrl();
        return await firstValueFrom(
          this.http.get<R>(`${baseUrl}${endpoint}`).pipe(
            catchError((err: HttpErrorResponse) => {
              throw this.handleError(err);
            })
          )
        );
      },
    });
  }

  /**
   * Create a resource for GET request with dynamic endpoint
   * @param endpointFn Function that returns the endpoint (called on each request)
   * @param request Optional reactive request signal/function that triggers reload when changed
   */
  public createDynamicGetResource<R = T>(endpointFn: () => string | null, request?: () => unknown): Resource<R> {
    return resource({
      request,
      loader: async () => {
        const endpoint = endpointFn();
        if (endpoint === null) {
          return [] as unknown as R;
        }
        const baseUrl = await this.getBaseUrl();
        return await firstValueFrom(
          this.http.get<R>(`${baseUrl}${endpoint}`).pipe(
            catchError((err: HttpErrorResponse) => {
              throw this.handleError(err);
            })
          )
        );
      },
    });
  }

  /**
   * Perform a POST request (async, non-reactive)
   * @param endpoint API endpoint (relative to baseUrl)
   * @param body Request body
   */
  public async post<R = T>(endpoint: string = '', body: unknown = {}, options?: ApiRequestOptions): Promise<R> {
    const baseUrl = await this.getBaseUrl();
    return await firstValueFrom(
      this.http.post<R>(`${baseUrl}${endpoint}`, body).pipe(
        catchError((err: HttpErrorResponse) => {
          throw this.handleError(err, options);
        })
      )
    );
  }

  /**
   * Perform a GET request (async, non-reactive)
   * @param endpoint API endpoint (relative to baseUrl)
   * @param options Optional query params
   */
  public async get<R = T>(
    endpoint: string = '',
    options?: { params?: Record<string, string> } & ApiRequestOptions
  ): Promise<R> {
    const baseUrl = await this.getBaseUrl();
    return await firstValueFrom(
      this.http.get<R>(`${baseUrl}${endpoint}`, options?.params ? { params: options.params } : undefined).pipe(
        catchError((err: HttpErrorResponse) => {
          throw this.handleError(err, options);
        })
      )
    );
  }

  /**
   * Perform a PUT request (async, non-reactive)
   * @param endpoint API endpoint (relative to baseUrl)
   * @param body Request body
   */
  public async put<R = T>(endpoint: string = '', body: unknown = {}, options?: ApiRequestOptions): Promise<R> {
    const baseUrl = await this.getBaseUrl();
    return await firstValueFrom(
      this.http.put<R>(`${baseUrl}${endpoint}`, body).pipe(
        catchError((err: HttpErrorResponse) => {
          throw this.handleError(err, options);
        })
      )
    );
  }

  /**
   * Perform a DELETE request (async, non-reactive)
   * @param endpoint API endpoint (relative to baseUrl)
   */
  public async delete<R = void>(endpoint: string = '', options?: ApiRequestOptions): Promise<R> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}${endpoint}`;
    const handleErr = (err: HttpErrorResponse): never => {
      throw this.handleError(err, options);
    };
    if (options?.responseType === 'text') {
      return (await firstValueFrom(this.http.delete(url, { responseType: 'text' }).pipe(catchError(handleErr)))) as R;
    }
    return await firstValueFrom(this.http.delete<R>(url).pipe(catchError(handleErr)));
  }

  /**
   * Perform a PATCH request (async, non-reactive)
   * @param endpoint API endpoint (relative to baseUrl)
   * @param body Request body
   */
  public async patch<R = T>(endpoint: string = '', body: unknown = {}, options?: ApiRequestOptions): Promise<R> {
    const baseUrl = await this.getBaseUrl();
    return await firstValueFrom(
      this.http.patch<R>(`${baseUrl}${endpoint}`, body).pipe(
        catchError((err: HttpErrorResponse) => {
          throw this.handleError(err, options);
        })
      )
    );
  }

  /**
   * Create a resource for POST request
   * @param endpoint API endpoint (relative to baseUrl)
   * @param body Request body or function that returns the body
   * @param request Optional reactive request signal/function that triggers reload when changed
   */
  public createPostResource<R = T>(
    endpoint: string = '',
    body: unknown | (() => unknown) = {},
    request?: () => unknown
  ): Resource<R> {
    return resource({
      request,
      loader: async () => {
        const baseUrl = await this.getBaseUrl();
        const requestBody = typeof body === 'function' ? body() : body;
        return await firstValueFrom(
          this.http.post<R>(`${baseUrl}${endpoint}`, requestBody).pipe(
            catchError((err: HttpErrorResponse) => {
              throw this.handleError(err);
            })
          )
        );
      },
    });
  }

  /**
   * Create a resource for PUT request
   * @param endpoint API endpoint (relative to baseUrl)
   * @param body Request body or function that returns the body
   * @param request Optional reactive request signal/function that triggers reload when changed
   */
  public createPutResource<R = T>(
    endpoint: string = '',
    body: unknown | (() => unknown) = {},
    request?: () => unknown
  ): Resource<R> {
    return resource({
      request,
      loader: async () => {
        const baseUrl = await this.getBaseUrl();
        const requestBody = typeof body === 'function' ? body() : body;
        return await firstValueFrom(
          this.http.put<R>(`${baseUrl}${endpoint}`, requestBody).pipe(
            catchError((err: HttpErrorResponse) => {
              throw this.handleError(err);
            })
          )
        );
      },
    });
  }

  /**
   * Create a resource for PATCH request
   * @param endpoint API endpoint (relative to baseUrl)
   * @param body Request body or function that returns the body
   * @param request Optional reactive request signal/function that triggers reload when changed
   */
  public createPatchResource<R = T>(
    endpoint: string = '',
    body: unknown | (() => unknown) = {},
    request?: () => unknown
  ): Resource<R> {
    return resource({
      request,
      loader: async () => {
        const baseUrl = await this.getBaseUrl();
        const requestBody = typeof body === 'function' ? body() : body;
        return await firstValueFrom(
          this.http.patch<R>(`${baseUrl}${endpoint}`, requestBody).pipe(
            catchError((err: HttpErrorResponse) => {
              throw this.handleError(err);
            })
          )
        );
      },
    });
  }

  /**
   * Create a resource for DELETE request
   * @param endpoint API endpoint (relative to baseUrl)
   * @param request Optional reactive request signal/function that triggers reload when changed
   */
  public createDeleteResource<R = void>(endpoint: string = '', request?: () => unknown): Resource<R> {
    return resource({
      request,
      loader: async () => {
        const baseUrl = await this.getBaseUrl();
        return await firstValueFrom(
          this.http.delete<R>(`${baseUrl}${endpoint}`).pipe(
            catchError((err: HttpErrorResponse) => {
              throw this.handleError(err);
            })
          )
        );
      },
    });
  }

  /**
   * Handle HTTP errors and return Error object
   * Uses ApiErrorService for application-level error handling (400-499, 500+)
   * Infrastructure-level errors (401, 403, network) are handled by the interceptor
   */
  protected handleError(err: HttpErrorResponse, options?: ApiRequestOptions): Error {
    // Skip handling for infrastructure-level errors (handled by interceptor)
    // and let interceptor handle 401, 403, and network errors
    if (err.status === 401 || err.status === 403 || !err.status) {
      // These are handled by the interceptor, just return Error for throwing
      const errorMessage =
        err.status === 0 ? 'Unable to connect to server' : err.error?.message || `Error ${err.status}`;
      return new Error(errorMessage);
    }

    // Handle application-level errors (400-499 client errors, 500+ server errors)
    if (!options?.suppressToast) {
      if (err.status >= 400 && err.status < 500) {
        // Client errors - show specific error message
        this.errorService.handleApiError(err);
      } else if (err.status >= 500) {
        // Server errors - show user-friendly message
        this.errorService.handleApiError(err);
      }
    }

    // Extract error message for throwing
    let errorMessage = 'An unexpected error occurred';

    if (err.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = err.error.message;
    } else {
      // Server-side error
      if (err.status === 0) {
        errorMessage = 'Unable to connect to server';
      } else if (err.status >= 400 && err.status < 500) {
        errorMessage = err.error?.message || `Client error: ${err.status}`;
      } else if (err.status >= 500) {
        errorMessage = err.error?.message || `Server error: ${err.status}`;
      }
    }

    return new Error(errorMessage);
  }
}
